import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";

const CompanyContext = createContext(null);

export function CompanyProvider({ children }) {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [currentId, setCurrentId] = useState(() => localStorage.getItem("nf_company") || null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (user === null) {
      // auth still checking — wait
      return;
    }
    if (user === false) {
      setCompanies([]);
      setCurrentId(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/companies");
      setCompanies(data);
      const ids = data.map((c) => c.id);
      const saved = localStorage.getItem("nf_company");
      if (saved && ids.includes(saved)) {
        setCurrentId(saved);
      } else {
        // Do NOT auto-pick — force explicit choice on first visit
        setCurrentId(null);
        localStorage.removeItem("nf_company");
      }
    } catch {
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const switchCompany = (id) => {
    setCurrentId(id);
    localStorage.setItem("nf_company", id);
  };

  const currentCompany = companies.find((c) => c.id === currentId) || null;

  const createCompany = async (payload) => {
    const { data } = await api.post("/companies", payload);
    await refresh();
    switchCompany(data.id);
    return data;
  };

  const updateCompany = async (id, payload) => {
    await api.patch(`/companies/${id}`, payload);
    await refresh();
  };

  const deleteCompany = async (id) => {
    await api.delete(`/companies/${id}`);
    localStorage.removeItem("nf_company");
    await refresh();
  };

  return (
    <CompanyContext.Provider value={{
      companies, currentId, currentCompany, loading,
      switchCompany, refresh, createCompany, updateCompany, deleteCompany,
    }}>
      {children}
    </CompanyContext.Provider>
  );
}

export const useCompany = () => useContext(CompanyContext);
